import { ReactNode, Suspense, useEffect, useRef } from 'react';

import { translate } from '@/i18n';
import {
  PermissionAccessLevel,
  PermissionName,
} from '@/modules/Settings/types/access-control.const.ts';
import { Loader } from '@/shared/components/interactions/Loader';
import { Typography } from '@/shared/components/ui/Typography';
import { useAccessControl } from '@/shared/hooks/access-control/useAccessControl.tsx';
import { classnames } from '@/shared/utils/classnames.ts';

import Warning from '@/assets/icons/alerts.svg?react';

interface RequiresPermissionProps {
  children: ReactNode;
  className?: string;
  name: (() => PermissionName) | PermissionName;
  observeSubTree?: boolean;
  showNoneNotice?: boolean;
}

export const RequiresPermission = ({
  children,
  className,
  name,
  observeSubTree,
  showNoneNotice,
}: RequiresPermissionProps) => {
  const viewRoot = useRef<HTMLDivElement>(null);
  const { getPermissionAccessLevel, loadingAccessLevels } = useAccessControl();
  const accessLevel =  getPermissionAccessLevel(typeof name === 'function' ? name() : name);

  const disableElementInteractivity = (element: Element) => {
    element.setAttribute('tabIndex', '-1');
    element.classList.add('pointer-events-none');
    element.querySelectorAll('*').forEach(node => {
      node.setAttribute('tabIndex', '-1');
      node.classList.add('pointer-events-none');
    });
  };

  useEffect(() => {
    let observer: MutationObserver;

    if (viewRoot.current !== null) {
      if (observeSubTree) {
        observer = new MutationObserver(() => {
          if (viewRoot.current !== null) {
            disableElementInteractivity(viewRoot.current);
          }
        });

        observer.observe(viewRoot.current, {
          childList: true,
          subtree: true,
        });
      }

      disableElementInteractivity(viewRoot.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [accessLevel, observeSubTree]);

  return loadingAccessLevels ? (
    <div className="grid h-full w-full flex-1 place-items-center p-2">
      <Loader appearance="inline" />
    </div>
  ) : accessLevel === PermissionAccessLevel.FULL ? (
    <Suspense fallback={null}>{children}</Suspense>
  ) : accessLevel === PermissionAccessLevel.VIEW ? (
    <div
      className={classnames('pointer-events-none relative opacity-50 grayscale', className)}
      ref={viewRoot}
      tabIndex={-1}
    >
      <Suspense fallback={null}>{children}</Suspense>
      <div className="absolute left-0 top-0 z-10 size-full" style={{ pointerEvents: 'all' }} />
    </div>
  ) : showNoneNotice ? (
    <div
      className={classnames(
        'flex h-full w-full flex-1 items-center justify-center gap-2 px-4 py-2',
        className,
      )}
    >
      <Warning />
      <Typography as="p" className="text-sm">
        {translate('accessControl.accessDenied')}
      </Typography>
    </div>
  ) : null;
};
